import { ModuleValueGeneral, SchemaModuleGeneral } from './module-general';
import { JSONSchema } from '../types/json-schema';
import { FormComponent } from '../form.component';
import { DATE_DAY } from '../constants';

import * as rawJSONSchemaModuleObstetrics from '../../schemas/module-obstetrics.json';

class ModuleValueObstetrics extends ModuleValueGeneral {
  edd_compute?: boolean;
  lmp?: string;
  edd?: string;

  date_of_delivery?: string;
  gestation_week_compute?: boolean;
  gestation_period_days?: number;

  date_of_obstetrics_procedure?: string;
  obstetrics_procedure_gestation_period_days?: number;

  discharge_date?: string;
  length_of_stay?: string;

  lactate_mmol_l?: number;
  lactate_mg_dl?: number;
  lactate_mg_dl_compute?: boolean;

  cord_lactate_mmol_l?: number;
  cord_lactate_mg_dl?: number;
  cord_lactate_mg_dl_compute?: boolean;
}

export class SchemaModuleObstetrics extends SchemaModuleGeneral<ModuleValueObstetrics> {
  schema: JSONSchema<ModuleValueObstetrics>;

  constructor() {
    super();

    this.loadSchema( rawJSONSchemaModuleObstetrics );
  }

  hook( form: FormComponent ) {
    super.hook( form );

    this.listenValue( 'edd_compute', this.fieldHandlerEDDCompute.bind( this ) );
    this.listenValue( [ 'lmp', 'edd' ], this.fieldHandlerLMPEDD.bind( this ) );

    this.listenValue( [ 'lmp', 'date_of_delivery' ], this.fieldHandlerGestationPeriod( 'gestation_period_days' ).bind( this ) );
    this.listenValue( [ 'lmp', 'date_of_obstetrics_procedure' ], this.fieldHandlerGestationPeriod( 'obstetrics_procedure_gestation_period_days' ).bind( this ) );

    this.listenValue( [ 'discharge_date', 'date_of_delivery' ], this.fieldHandlerDischargeDate( 'length_of_stay' ).bind( this ) );

    this.listenValue( 'lactate_mg_dl_compute', this.fieldHandlerLactateCompute( 'lactate_mg_dl_compute', 'lactate_mmol_l', 'lactate_mg_dl' ).bind( this ) );
    this.listenValue( [ 'lactate_mmol_l', 'lactate_mg_dl' ], this.fieldHandlerLactate( 'lactate_mg_dl_compute', 'lactate_mmol_l', 'lactate_mg_dl' ).bind( this ) );

    this.listenValue( 'cord_lactate_mg_dl_compute', this.fieldHandlerLactateCompute( 'cord_lactate_mg_dl_compute', 'cord_lactate_mmol_l', 'cord_lactate_mg_dl' ).bind( this ) );
    this.listenValue( [ 'cord_lactate_mmol_l', 'cord_lactate_mg_dl' ], this.fieldHandlerLactate( 'cord_lactate_mg_dl_compute', 'cord_lactate_mmol_l', 'cord_lactate_mg_dl' ).bind( this ) );
  }

  fieldHandlerEDDCompute( compute: boolean ) {
    if ( typeof compute === 'undefined' || compute === null ) return;
    this.schema.properties[ 'edd' ].readOnly = compute;
    this.schema.properties[ 'lmp' ].readOnly = !compute;

    this.fieldHandlerLMPEDD( this.getValue( 'lmp' ), this.getValue( 'edd' ) );
  }

  fieldHandlerLMPEDD( lmpString: string, eddString: string ) {
    if ( this.getValue( 'edd_compute' ) ) {
      this.calculateEDD( lmpString );
    } else {
      this.calculateLMP( eddString );
    }
  }

  calculateEDD( lmpString: string ) {
    let edd = new Date( lmpString );
    edd = this.modifyDate( edd, DATE_DAY * 280 );
    let eddString = this.getShortDate( edd );
    this.updateValue( 'edd', eddString || '' );
  }

  calculateLMP( eddString: string ) {
    let lmp = new Date( eddString );
    lmp = this.modifyDate( lmp, - DATE_DAY * 280 );
    let lmpString = this.getShortDate( lmp );
    this.updateValue( 'lmp', lmpString || '' );
  }

  modifyDate( date: Date, change: number ) {
    if ( date && isNaN( date.valueOf() ) ) return null;
    return new Date( date.valueOf() + change );
  }

  getShortDate( date: Date ) {
    return ( date && !isNaN( date.valueOf() ) ) ? date.toISOString().split( 'T' )[ 0 ] : null;
  }

  fieldHandlerGestationPeriod<K extends keyof ModuleValueObstetrics>( gestationPeriodProperty: K ) {
    return ( lmpString: string, deliveryDateString: string ) => {
      const value = this.calculateGestationDays( lmpString, deliveryDateString ) as ModuleValueObstetrics[ K ];
      this.updateValue( gestationPeriodProperty, value );
    };
  }

  calculateGestationDays( lmpString: string, deliveryDateString: string ) {
    let lmp = new Date( lmpString );
    let deliveryDate = new Date( deliveryDateString );

    let gestation_days = Math.ceil( ( deliveryDate.valueOf() - lmp.valueOf() ) / DATE_DAY );

    if ( isNaN( gestation_days ) || gestation_days < 0 ) {
      gestation_days = null;
    }

    return gestation_days;
  }

  fieldHandlerLactateCompute<K extends keyof ModuleValueObstetrics>( computeProperty: K, mmolLProperty: K, mgdLProperty: K ) {
    return ( compute: boolean ) => {
      if ( typeof compute === 'undefined' || compute === null ) return;
      this.schema.properties[ mgdLProperty ].readOnly = compute;
      this.schema.properties[ mmolLProperty ].readOnly = !compute;

      this.fieldHandlerLactate( computeProperty, mmolLProperty, mgdLProperty )( this.getValue( mmolLProperty ) as number, this.getValue( mgdLProperty ) as number );
    }
  }

  fieldHandlerLactate<K extends keyof ModuleValueObstetrics>( computeProperty: K, mmolLProperty: K, mgdLProperty: K ) {
    return ( mmolL: number, mgdL: number ) => {
      // mgdL = mmolL * 18;
      if ( this.getValue( computeProperty ) ) {
        this.updateValue( mgdLProperty, ( mmolL === null ? null : mmolL * 18 ) as ModuleValueObstetrics[ K ] );
      } else {
        this.updateValue( mmolLProperty, ( mgdL === null ? null : mgdL / 18 ) as ModuleValueObstetrics[ K ] );
      }
    };
  }

  setupValidators() {
    super.setupValidators();

    this.validatorDateComparison( 'date_of_delivery', 'lmp', '>' );
    this.validatorDateComparison( 'date_of_obstetrics_procedure', 'lmp', '>' );
    this.validatorDateComparison( 'discharge_date', 'lmp', '>' );
    this.validatorDateComparison( 'discharge_date', 'date_of_delivery', '>=' );

    this.validatorDate( 'date_of_birth', ( new Date() ), '<=' , v => new Date( v as any ));
  }

}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/forms/module-obstetrics.ts